/**
 * Применяет пресет настроек карточки (GenerationCard.settings) к state
 * генератора через updateSetting. Единая точка для всех мест, где карточка
 * раскрывается в виджет: applyCardToGenerate (виджет /bots), inline applyCard
 * в Generate.tsx (страница /generate) и бутстрап с главной (GenerateContext).
 *
 * Применяются ТОЛЬКО заданные ключи. Для booleans — проверка `!== undefined`
 * (иначе осознанный `false` от админа не применился бы). Незаданные ключи
 * оставляют дефолты виджета / значения от выбранной модели.
 *
 * Значения приходят строками из БД (config.cards[].settings, JSONB), поэтому
 * enum-поля (outputFormat/speed/safetyFilter) кастуются в литералы — downstream
 * payload-builders толерантны к произвольной строке, а updateSetting не валидирует.
 */
import type { GenerationCardSettings } from '../../../types/generationCard'
import type { GenerateSettings } from '../hooks/useGenerateSettings'

type UpdateSetting = <K extends keyof GenerateSettings>(key: K, value: GenerateSettings[K]) => void

export function applyCardSettings(s: GenerationCardSettings, updateSetting: UpdateSetting): void {
	// Общие (image/video)
	if (s.aspectRatio) updateSetting('aspectRatio', s.aspectRatio)
	if (s.resolution) updateSetting('resolution', s.resolution)
	if (s.duration) updateSetting('duration', s.duration)
	if (s.outputFormat) updateSetting('outputFormat', s.outputFormat as GenerateSettings['outputFormat'])
	if (s.quality) updateSetting('quality', s.quality)
	if (s.negativePrompt !== undefined) updateSetting('negativePrompt', s.negativePrompt)
	if (s.safetyFilter) updateSetting('safetyFilter', s.safetyFilter as GenerateSettings['safetyFilter'])
	if (s.promptOptimizer !== undefined) updateSetting('promptOptimizer', s.promptOptimizer)
	if (s.googleSearch !== undefined) updateSetting('googleSearch', s.googleSearch)
	// Midjourney
	if (s.version) updateSetting('version', s.version)
	if (s.speed) updateSetting('speed', s.speed as GenerateSettings['speed'])
	if (s.stylization !== undefined) updateSetting('stylization', s.stylization)
	// Video sound
	if (s.sound !== undefined) updateSetting('sound', s.sound)
	// Seedance 2: режим «кадры | референсы». На свежей навигации с главной
	// аплоадов ещё нет, поэтому очищающие сайд-эффекты switchSeedanceMode не нужны.
	if (s.seedanceMode) updateSetting('seedanceMode', s.seedanceMode)
	// Audio (Suno)
	if (s.audioTitle !== undefined) updateSetting('audioTitle', s.audioTitle)
	if (s.audioStyle !== undefined) updateSetting('audioStyle', s.audioStyle)
	if (s.audioInstrumental !== undefined) updateSetting('audioInstrumental', s.audioInstrumental)
	if (s.audioVocalGender !== undefined) updateSetting('audioVocalGender', s.audioVocalGender)
	if (s.audioCustomMode !== undefined) updateSetting('audioCustomMode', s.audioCustomMode)
}
